/**
 * Consent and data minimization boundary for product analytics. DataFast is
 * deliberately isolated here: callers can emit only allowlisted product and paywall
 * goals, and route code can submit only a sanitized virtual page path.
 */
import type { DataFastWeb } from "datafast";
import { env } from "@/shared/config/env";
import {
  clearDataFastBrowserState,
  type AnalyticsConsent,
  prepareDataFastBrowserStateForConsent,
  readAnalyticsConsentSnapshot,
  readCookie,
  readSeenAnalyticsConsentEpoch,
  resumeDataFastBrowserStateForConsent,
  subscribeToAnalyticsConsent,
  writeSeenAnalyticsConsentEpoch,
  writeAnalyticsConsent,
} from "./analyticsConsent";

type PaywallProperties = {
  variant?: unknown;
  layout?: unknown;
  kind?: unknown;
  plan?: unknown;
  model?: unknown;
};

const PRODUCT_GOALS = [
  "signup_completed", "project_created", "asset_uploaded", "generation_submitted",
  "checkout_started", "asset_download_requested", "share_created",
] as const;
export type ProductGoal = (typeof PRODUCT_GOALS)[number];
export type AnalyticsEvent =
  | { name: ProductGoal; properties?: never }
  | { name: "paywall_viewed"; properties: PaywallProperties }
  | { name: "paywall_upgrade_clicked"; properties: PaywallProperties };

export interface CheckoutAttribution {
  datafast_visitor_id: string;
  datafast_session_id: string;
}

const STATIC_APP_PATHS = new Set([
  "/",
  "/activity",
  "/assets",
  "/audio",
  "/pricing",
  "/projects",
  "/settings",
  "/sign-in",
  "/sign-up",
  "/spaces",
  "/tools",
]);
const PROJECT_ROOMS = new Set(["image", "library", "video"]);
const GENERATION_MEDIA = new Set(["audio", "image", "video"]);
const PAYWALL_VARIANTS = new Set(["a", "b", "c", "d", "fw", "fs", "fo"]);
const PAYWALL_LAYOUTS = new Set(["bottom", "immersive", "split", "full-wall", "full-split", "full-offer"]);
const PAYWALL_KINDS = new Set(["img", "vid"]);
const PAYWALL_PLANS = new Set([
  "creator",
  "pro_5k",
  "ultra_13k",
  "ultra_26k",
  "ultra_40k",
]);
const SAFE_ROOT_REFERRER_PATHS = new Set([
  ...STATIC_APP_PATHS,
  "/404",
  "/about",
  "/contact",
  "/delete-account",
  "/faq",
  "/features",
  "/privacy",
  "/terms",
]);
const UUID_V4 =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SESSION_ID =
  /^s[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

let client: DataFastWeb | null = null;
let clientPromise: Promise<DataFastWeb | null> | null = null;
let revocationPromise: Promise<void> | null = null;
let customEventChain: Promise<void> = Promise.resolve();
let consentRevision = 0;

interface AnalyticsLocation {
  pathname: string;
  search: string;
}

function pathnameOnly(pathname: string): string {
  const separator = pathname.search(/[?#]/);
  return separator === -1 ? pathname : pathname.slice(0, separator);
}

function searchFrom(pathname: string, search: string): string {
  if (search) return search;
  const queryStart = pathname.indexOf("?");
  if (queryStart === -1) return "";
  const hashStart = pathname.indexOf("#", queryStart);
  return pathname.slice(queryStart, hashStart === -1 ? undefined : hashStart);
}

/** Tokens in these public URLs are bearer credentials and never enter analytics. */
export function isAnalyticsExcludedPath(
  pathname: string,
  search = "",
): boolean {
  const path = pathnameOnly(pathname);
  const query = new URLSearchParams(searchFrom(pathname, search));
  return (
    (path.startsWith("/shares/") && path.length > "/shares/".length) ||
    (path.startsWith("/v1/shares/") && path.length > "/v1/shares/".length) ||
    (path.startsWith("/project-shares/") && path.length > "/project-shares/".length) ||
    (path.startsWith("/v1/project-shares/") && path.length > "/v1/project-shares/".length) ||
    (path.startsWith("/phone-upload/") &&
      path.length > "/phone-upload/".length) ||
    (path.startsWith("/v1/phone-uploads/") &&
      path.length > "/v1/phone-uploads/".length) ||
    query.has("invite")
  );
}

/** Map application URLs to an allowlisted, identifier-free virtual route. */
export function sanitizeAppPageview(
  pathname: string,
  search = "",
): string | null {
  if (isAnalyticsExcludedPath(pathname, search)) return null;
  const path = pathnameOnly(pathname);
  if (STATIC_APP_PATHS.has(path)) {
    return path === "/" ? "/app" : `/app${path}`;
  }

  const segments = path.split("/").filter(Boolean);
  if (
    segments.length === 2 &&
    segments[0] === "generate" &&
    GENERATION_MEDIA.has(segments[1])
  ) {
    return `/app/generate/${segments[1]}`;
  }
  if (segments[0] === "projects" && segments.length >= 2) {
    if (segments.length === 2) return "/app/projects/:project_id";
    if (segments.length === 3 && PROJECT_ROOMS.has(segments[2])) {
      return `/app/projects/:project_id/${segments[2]}`;
    }
  }
  return "/app/not-found";
}

/** Preserve the app subdomain while keeping the virtual URL query/hash-free. */
export function sanitizedAppPageviewHref(
  pathname: string,
  search = "",
  origin = window.location.origin,
): string | null {
  const path = sanitizeAppPageview(pathname, search);
  if (!path) return null;
  try {
    const parsedOrigin = new URL(origin);
    if (!["http:", "https:"].includes(parsedOrigin.protocol)) return null;
    return new URL(path, parsedOrigin.origin).toString();
  } catch {
    return null;
  }
}

/** Resolve the matching environment's privacy controls without a prod fallback. */
export function analyticsPrivacyHref(baseUrl = env.LANDING_BASE_URL): string {
  const sameOrigin =
    typeof window === "undefined" ? "" : window.location.origin;
  try {
    return new URL(
      "/privacy#analytics-cookies",
      baseUrl || sameOrigin,
    ).toString();
  } catch {
    return "/privacy#analytics-cookies";
  }
}

function isSafeInitialReferrer(): boolean {
  if (typeof document === "undefined" || !document.referrer) return true;
  try {
    const referrer = new URL(document.referrer);
    if (
      referrer.username ||
      referrer.password ||
      referrer.search ||
      referrer.hash
    ) {
      return false;
    }
    const configuredDomain = env.DATAFAST_DOMAIN.replace(
      /^\./,
      "",
    ).toLowerCase();
    const sameRoot =
      referrer.hostname === configuredDomain ||
      referrer.hostname.endsWith(`.${configuredDomain}`);
    if (!sameRoot) return referrer.pathname === "/";
    if (SAFE_ROOT_REFERRER_PATHS.has(referrer.pathname)) return true;
    const segments = referrer.pathname.split("/").filter(Boolean);
    return (
      segments.length === 2 &&
      segments[0] === "generate" &&
      GENERATION_MEDIA.has(segments[1])
    );
  } catch {
    return false;
  }
}

function currentAnalyticsLocation(): AnalyticsLocation {
  return {
    pathname: window.location.pathname,
    search: window.location.search,
  };
}

function canUseAnalytics(location?: AnalyticsLocation): boolean {
  if (typeof window === "undefined") return false;
  const route = location ?? currentAnalyticsLocation();
  const consent = readAnalyticsConsentSnapshot();
  return (
    !env.AUTH_BYPASS &&
    !env.GENERATION_VISUAL_PREVIEW &&
    Boolean(env.DATAFAST_WEBSITE_ID && env.DATAFAST_DOMAIN) &&
    consent.choice === "accepted" &&
    consent.epoch !== null &&
    readSeenAnalyticsConsentEpoch() === consent.epoch &&
    !isAnalyticsExcludedPath(route.pathname, route.search)
  );
}

function resumeOriginForExistingConsent(): void {
  resumeDataFastBrowserStateForConsent();
}

function prepareOriginForNewConsent(): void {
  prepareDataFastBrowserStateForConsent(env.DATAFAST_DOMAIN);
}

function reconcileConsentCycle(): void {
  const consent = readAnalyticsConsentSnapshot();
  const seenEpoch = readSeenAnalyticsConsentEpoch();
  if (!consent.epoch || !consent.choice) {
    revokeAnalyticsClient();
    writeSeenAnalyticsConsentEpoch(null);
    return;
  }
  if (seenEpoch === consent.epoch) {
    if (consent.choice === "accepted") resumeOriginForExistingConsent();
    return;
  }

  revokeAnalyticsClient();
  if (consent.choice === "accepted") prepareOriginForNewConsent();
  writeSeenAnalyticsConsentEpoch(consent.epoch);
}

function reconcileObservedConsentMutation(
  choice: AnalyticsConsent | null,
  epoch: string | null,
): void {
  revokeAnalyticsClient();
  if (choice === "accepted" && epoch) prepareOriginForNewConsent();
  writeSeenAnalyticsConsentEpoch(epoch);
}

async function getClient(
  location?: AnalyticsLocation,
): Promise<DataFastWeb | null> {
  reconcileConsentCycle();
  if (revocationPromise) await revocationPromise;
  reconcileConsentCycle();
  if (client) return client;
  if (!canUseAnalytics(location) || !isSafeInitialReferrer()) return null;
  if (clientPromise) {
    const initialized = await clientPromise;
    if (initialized || !canUseAnalytics(location)) return initialized;
    return getClient(location);
  }
  resumeOriginForExistingConsent();
  const initializationRevision = consentRevision;
  const pending = import("datafast")
    .then(async ({ initDataFast }) => {
      if (
        initializationRevision !== consentRevision ||
        !canUseAnalytics(location)
      ) {
        return null;
      }
      const initialized = await initDataFast({
        websiteId: env.DATAFAST_WEBSITE_ID,
        domain: env.DATAFAST_DOMAIN,
        allowLocalhost: env.DATAFAST_ALLOW_LOCALHOST,
        autoCapturePageviews: false,
        debug: false,
      });
      if (
        initializationRevision !== consentRevision ||
        !canUseAnalytics(location) ||
        !canUseAnalytics()
      ) {
        try {
          await initialized.optOut();
        } finally {
          clearDataFastBrowserState(env.DATAFAST_DOMAIN);
        }
        return null;
      }
      client = initialized;
      return initialized;
    })
    .catch(() => null);
  clientPromise = pending;
  void pending.then(() => {
    if (clientPromise === pending) clientPromise = null;
  });
  return pending;
}

function safeEnum(
  value: unknown,
  allowed: ReadonlySet<string>,
): string | undefined {
  return typeof value === "string" && allowed.has(value) ? value : undefined;
}

function safeLabel(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim().normalize("NFKC");
  const hasControlCharacter = [...normalized].some((character) => {
    const code = character.charCodeAt(0);
    return code <= 0x1f || code === 0x7f;
  });
  if (
    !normalized ||
    normalized.length > 80 ||
    hasControlCharacter ||
    /[@<>{}\\/?#&=]/.test(normalized) ||
    UUID_V4.test(normalized) ||
    /\b(?:bearer|token|secret|api[_ -]?key|signature)\b/i.test(normalized)
  ) {
    return undefined;
  }
  return normalized;
}

/** Strict allowlist for the only custom goal metadata Afterform emits. */
export function sanitizePaywallProperties(
  properties: PaywallProperties,
): Record<string, string> {
  const result: Record<string, string> = {};
  const variant = safeEnum(properties.variant, PAYWALL_VARIANTS);
  const layout = safeEnum(properties.layout, PAYWALL_LAYOUTS);
  const kind = safeEnum(properties.kind, PAYWALL_KINDS);
  const plan = safeEnum(properties.plan, PAYWALL_PLANS);
  const model = safeLabel(properties.model);
  if (variant) result.variant = variant;
  if (layout) result.layout = layout;
  if (kind) result.kind = kind;
  if (plan) result.plan = plan;
  if (model) result.model = model;
  return result;
}

/** Track a manually sanitized SPA pageview; queries and hashes are never sent. */
export function trackAppPageview(pathname: string, search = ""): void {
  const location = { pathname, search };
  reconcileConsentCycle();
  const trackingRevision = consentRevision;
  const path = sanitizeAppPageview(pathname, search);
  const href = sanitizedAppPageviewHref(pathname, search);
  if (!path || !href || !canUseAnalytics(location)) return;
  void getClient(location).then((analytics) => {
    if (analytics && canUseAnalytics(location) && canUseAnalytics()) {
      if (trackingRevision !== consentRevision) return;
      void analytics.trackScreen(path, { href });
    }
  });
}

/** Track one of the explicitly approved product goals. */
export function track(
  name: AnalyticsEvent["name"],
  properties: PaywallProperties = {},
): void {
  reconcileConsentCycle();
  if (!canUseAnalytics()) return;
  const trackingRevision = consentRevision;
  if (!PRODUCT_GOALS.includes(name as ProductGoal) && name !== "paywall_viewed" && name !== "paywall_upgrade_clicked") return;
  const sanitized = PRODUCT_GOALS.includes(name as ProductGoal) ? {} : sanitizePaywallProperties(properties);
  customEventChain = customEventChain
    .then(async () => {
      if (trackingRevision !== consentRevision || !canUseAnalytics()) return;
      const analytics = await getClient();
      if (
        analytics &&
        trackingRevision === consentRevision &&
        canUseAnalytics()
      ) {
        await analytics.track(name, sanitized);
      }
    })
    .catch(() => undefined);
}

function revokeAnalyticsClient(): void {
  consentRevision += 1;
  const activeClient = client;
  client = null;
  clearDataFastBrowserState(env.DATAFAST_DOMAIN);
  if (activeClient) {
    const pendingRevocation = activeClient
      .optOut()
      .catch(() => undefined)
      .finally(() => clearDataFastBrowserState(env.DATAFAST_DOMAIN));
    revocationPromise = pendingRevocation;
    void pendingRevocation.finally(() => {
      if (revocationPromise === pendingRevocation) revocationPromise = null;
    });
  }
}

/** Keep a mounted surface synchronized with consent changes in other contexts. */
export function observeAnalyticsConsent(
  observer?: (choice: AnalyticsConsent | null) => void,
): () => void {
  reconcileConsentCycle();
  return subscribeToAnalyticsConsent((choice, epoch) => {
    // The persisted seen epoch is shared by same-origin tabs, so every tab
    // must still revoke its own in-memory client when the snapshot changes.
    reconcileObservedConsentMutation(choice, epoch);
    observer?.(choice);
  });
}

/** Persist consent at the configured root domain and update the live adapter. */
export function setAnalyticsConsent(choice: AnalyticsConsent): void {
  if (choice === "accepted") {
    const consent = readAnalyticsConsentSnapshot();
    const uninterrupted =
      consent.choice === "accepted" &&
      consent.epoch !== null &&
      readSeenAnalyticsConsentEpoch() === consent.epoch;
    consentRevision += 1;
    if (uninterrupted) resumeOriginForExistingConsent();
    else {
      revokeAnalyticsClient();
      prepareOriginForNewConsent();
    }
    writeAnalyticsConsent(choice, env.DATAFAST_DOMAIN);
    return;
  }
  revokeAnalyticsClient();
  writeAnalyticsConsent(choice, env.DATAFAST_DOMAIN);
}

/** Anonymous, format-validated attribution for the server-created Polar checkout. */
export function getCheckoutAttribution(): CheckoutAttribution | undefined {
  reconcileConsentCycle();
  if (!canUseAnalytics()) return undefined;
  const visitorID = readCookie("datafast_visitor_id");
  const sessionID = readCookie("datafast_session_id");
  if (
    !visitorID ||
    !sessionID ||
    !UUID_V4.test(visitorID) ||
    !SESSION_ID.test(sessionID)
  ) {
    return undefined;
  }
  return {
    datafast_visitor_id: visitorID,
    datafast_session_id: sessionID,
  };
}

export function resetAnalyticsForTests(): void {
  consentRevision += 1;
  client = null;
  clientPromise = null;
  revocationPromise = null;
  customEventChain = Promise.resolve();
}
